import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ERRORS } from '@/common/constants/errors';
import { useGetReimbursementOrderDetails } from '@/common/integrations/menhir/employee/orders/orders.hooks';
import { TOrderCategorySlug } from '@/common/integrations/menhir/employee/orders/orders.types';
import { IReimbursableBenefit } from '@/common/types/catalog';
import { ReimburseableMapper } from '@/common/utils/catalog';
import { formatDate } from '@/common/utils/date';
import { LoadingPlaceholder } from '@/web/components/LoadingPlaceholder/LoadingPlaceholder';
import { CancelReimbursementModal } from '@/web/containers/ReimbursementDetails/containers/CancelReimbursementModal/CancelReimbursementModal';
import {
  Badge,
  Button,
  CardActionFile,
  Drawer,
  Flex,
  IconBadge,
  IconFile,
  LabeledValueList,
  Text,
} from '@coverflex-tech/hypernova';
import { MoneyUtils } from '@coverflex-tech/hypernova-utils';

export const ReimbursementDetaiDrawer = ({
  onClose,
  orderId,
  categorySlug,
}: IReimbursementDetailDrawerProps) => {
  const { t } = useTranslation();

  const { reimbursementDetails, isInitialLoading } =
    useGetReimbursementOrderDetails({
      category: categorySlug,
      orderId: orderId,
    });

  const [displayCancelModal, toggleCancelModal] = useState(false);

  if (isInitialLoading) {
    return <LoadingPlaceholder />;
  }

  if (!reimbursementDetails) {
    throw new Error(ERRORS.REIMBURSEMENT_ORDER_DETAILS_REQUIRED);
  }

  const badgeStatus = (() => {
    switch (reimbursementDetails.status) {
      case 'approved':
      case 'completed':
        return 'success';
      case 'pending_transfer':
      case 'pending_approval':
        return 'info';
      default:
        return 'disabled';
    }
  })();

  const deductedAmount = reimbursementDetails.vatAmount
    ? MoneyUtils.subtract(
        reimbursementDetails.amount,
        reimbursementDetails.vatAmount
      )
    : undefined;

  const drawerTitle =
    categorySlug === 'budgets'
      ? t('dashboard.reimbursement_details.drawer_title', {
          category: reimbursementDetails?.budgetEmployee?.budget?.name,
        })
      : t('dashboard.reimbursement_details.drawer_title', {
          category: t(`general.benefits.${categorySlug}`),
        });

  return (
    <>
      {displayCancelModal && (
        <CancelReimbursementModal
          movementCategory={
            ReimburseableMapper[categorySlug as IReimbursableBenefit]
          }
          orderId={reimbursementDetails.id}
          category={categorySlug}
          onCancel={() => toggleCancelModal(false)}
          onClose={() => toggleCancelModal(false)}
        />
      )}
      <Drawer onClose={onClose} title={drawerTitle}>
        <Flex flexDirection="column" gap="lg">
          <Flex>
            <Badge status={badgeStatus} size="lg">
              {t(
                `dashboard.reimbursement_details.status_badge.${reimbursementDetails.status}`
              )}
            </Badge>
          </Flex>
          <LabeledValueList.Root
            title={t('dashboard.reimbursement_details.invoice_details')}
            solid
          >
            <LabeledValueList.Item
              label={t('dashboard.reimbursement_details.invoice_amount')}
              value={MoneyUtils.moneyToString(reimbursementDetails.amount)}
            />
            <LabeledValueList.Item
              label={t('dashboard.reimbursement_details.invoice_date')}
              value={formatDate(reimbursementDetails.receiptDate)}
            />
            <LabeledValueList.Item
              label={t('dashboard.reimbursement_details.request_date')}
              value={formatDate(reimbursementDetails.insertedAt)}
            />
            {reimbursementDetails.vatAmount ? (
              <LabeledValueList.Item
                label={t('dashboard.reimbursement_details.vat_amount')}
                value={MoneyUtils.moneyToString(reimbursementDetails.vatAmount)}
              />
            ) : null}
            {deductedAmount ? (
              <LabeledValueList.Item
                label={t('dashboard.reimbursement_details.deducted_amount')}
                value={MoneyUtils.moneyToString(deductedAmount)}
              />
            ) : null}
          </LabeledValueList.Root>

          {reimbursementDetails.product.category.slug ===
            'retirement-investment' &&
            reimbursementDetails.reimbursableItem && (
              <LabeledValueList.Root
                title={t('dashboard.reimbursement_details.product_details')}
                solid
              >
                <LabeledValueList.Item
                  label={t(
                    'dashboard.reimbursement_details.product_details_provider'
                  )}
                  value={reimbursementDetails.reimbursableItem.provider}
                />
                <LabeledValueList.Item
                  label={t(
                    'dashboard.reimbursement_details.product_details_product_name'
                  )}
                  value={reimbursementDetails.reimbursableItem.name}
                />
              </LabeledValueList.Root>
            )}

          {reimbursementDetails.product.category.slug === 'transportation' &&
            reimbursementDetails.transportationCard && (
              <LabeledValueList.Root
                title={t('dashboard.reimbursement_details.transportation_card')}
                solid
              >
                <LabeledValueList.Item
                  label={t(
                    'dashboard.reimbursement_details.transportation_card_number'
                  )}
                  value={reimbursementDetails.transportationCard.number}
                />
                <LabeledValueList.Item
                  label={t(
                    'dashboard.reimbursement_details.transportation_card_expiration_date'
                  )}
                  value={formatDate(
                    reimbursementDetails.transportationCard.expirationDate
                  )}
                />
              </LabeledValueList.Root>
            )}
          {reimbursementDetails.description && (
            <LabeledValueList.Item
              label={t('dashboard.reimbursement_details.description')}
              value={reimbursementDetails.description}
            />
          )}
          <LabeledValueList.Root
            title={t('dashboard.reimbursement_details.reimbursement_details')}
            solid
          >
            {reimbursementDetails.customFields &&
              reimbursementDetails.customFields.map((field) => {
                const value =
                  field.inputType === 'checkbox'
                    ? t(`general.values.${!!field.value ? 'yes' : 'no'}`)
                    : field.value;
                return (
                  value !== null &&
                  value !== undefined && (
                    <LabeledValueList.Item
                      key={field.id}
                      label={field.label}
                      value={value}
                    />
                  )
                );
              })}

            <LabeledValueList.Item
              label={t('dashboard.reimbursement_details.reference')}
              value={reimbursementDetails.reference}
            />
            {reimbursementDetails.status === 'approved' &&
              reimbursementDetails.statusDetails?.date && (
                <LabeledValueList.Item
                  label={t('dashboard.reimbursement_details.approval_date')}
                  value={formatDate(reimbursementDetails.statusDetails.date)}
                />
              )}
            {reimbursementDetails.status !== 'rejected' && (
              <LabeledValueList.Item
                label={t('dashboard.reimbursement_details.transfer_to')}
                value={reimbursementDetails.iban}
              />
            )}
          </LabeledValueList.Root>

          {reimbursementDetails.status === 'rejected' && (
            <LabeledValueList.Root
              title={t('dashboard.reimbursement_details.rejected_reason')}
              solid
            >
              <LabeledValueList.Item
                label={reimbursementDetails.statusDetails?.reason || ''}
                value=""
              />
            </LabeledValueList.Root>
          )}

          {reimbursementDetails.status === 'cancelled' && (
            <LabeledValueList.Root
              title={t('dashboard.reimbursement_details.cancelation_details')}
              solid
            >
              <LabeledValueList.Item
                label={t('dashboard.reimbursement_details.canceled_at')}
                value={formatDate(reimbursementDetails.statusDetails?.date)}
              />
            </LabeledValueList.Root>
          )}

          <Flex flexDirection="column" gap="lg">
            {reimbursementDetails.files.map((file) => (
              <CardActionFile
                key={file.id}
                title={file.name}
                type="download"
                size="sm"
                fileUrl={file.url}
                actionLabel={t('general.action.download')}
                icon={
                  <IconBadge
                    icon={<IconFile />}
                    ratio={64}
                    backgroundColor="neutral20"
                  />
                }
              />
            ))}
          </Flex>

          <Flex flexDirection="column" gap="lg">
            {reimbursementDetails.supportFiles &&
              reimbursementDetails.supportFiles.length > 0 && (
                <>
                  <Text bold size="md">
                    {t(
                      'dashboard.reimbursement_details.additional_files.title'
                    )}
                  </Text>
                  {reimbursementDetails.supportFiles.map((file) => (
                    <CardActionFile
                      key={file.id}
                      title={file.name}
                      type="download"
                      size="sm"
                      fileUrl={file.url}
                      actionLabel={t('general.action.download')}
                      icon={
                        <IconBadge
                          icon={<IconFile />}
                          ratio={64}
                          backgroundColor="neutral20"
                        />
                      }
                    />
                  ))}
                </>
              )}
          </Flex>
          {reimbursementDetails.status === 'pending_approval' && (
            <div>
              <div>
                {t('dashboard.reimbursement_details.cancel_disclaimer')}
              </div>
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => toggleCancelModal(true)}
              >
                {t('dashboard.reimbursement_details.cancel_request')}
              </Button>
            </div>
          )}
        </Flex>
      </Drawer>
    </>
  );
};

interface IReimbursementDetailDrawerProps {
  isLoading?: boolean;
  onClose: () => void;
  orderId: string;
  categorySlug: TOrderCategorySlug;
}
